import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader.jsx';
import Modal from '../../components/Modal.jsx';
import Empty from '../../components/Empty.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { sitesApi } from '../../api/sites.js';
import { apiError } from '../../api/client.js';

export default function SitesPage() {
  const { workspace } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    if (!workspace) return;
    setLoading(true);
    try {
      setSites(await sitesApi.list(workspace.id));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id]);

  return (
    <>
      <PageHeader
        title="Sites"
        description="Each site is a standalone web property — pages, products, theme, and domain."
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            New site
          </button>
        }
      />

      <div className="px-8 py-8">
        {loading ? (
          <div className="text-sm text-ink-500">Loading sites…</div>
        ) : sites.length === 0 ? (
          <Empty
            title="No sites yet"
            description="Spin one up to start designing pages, hooking up commerce, and shipping."
            action={
              <button onClick={() => setShowCreate(true)} className="btn-primary">
                Create your first site
              </button>
            }
          />
        ) : (
          <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sites.map((s) => (
              <SiteCard key={s.id} site={s} onChanged={load} />
            ))}
          </ul>
        )}
      </div>

      <CreateSiteModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(site) => {
          setShowCreate(false);
          setSites((cur) => [site, ...cur]);
        }}
      />
    </>
  );
}

function SiteCard({ site }) {
  return (
    <li className="card flex flex-col p-5 transition hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{site.name}</h3>
          <div className="mt-0.5 text-xs text-ink-500">/s/{site.slug}</div>
        </div>
        <span
          className={
            'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ' +
            (site.is_published
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-300')
          }
        >
          {site.is_published ? 'Published' : 'Draft'}
        </span>
      </div>

      <div className="mt-4 flex items-center gap-4 text-xs text-ink-500">
        <span>{site.pages_count ?? 0} pages</span>
        <span className="opacity-30">·</span>
        <span>Locale: {site.locale}</span>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <OpenEditorLink siteId={site.id} className="btn-primary flex-1 justify-center" />
        {site.is_published && (
          <a
            href={`${import.meta.env.VITE_API_URL || ''}/s/${site.slug}`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary"
          >
            View
          </a>
        )}
      </div>
    </li>
  );
}

function OpenEditorLink({ siteId, className }) {
  const [busy, setBusy] = useState(false);

  async function open(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const site = await sitesApi.show(siteId);
      const home = site.pages.find((p) => p.is_home) ?? site.pages[0];
      if (!home) {
        toast.error('No pages in this site yet.');
        return;
      }
      window.location.href = `/editor/${site.id}/${home.id}`;
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <a href="#" onClick={open} className={className}>
      {busy ? 'Opening…' : 'Open editor'}
    </a>
  );
}

function CreateSiteModal({ open, onClose, onCreated }) {
  const { workspace } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!workspace) return;
    setBusy(true);
    try {
      const site = await sitesApi.create(workspace.id, { name });
      toast.success('Site created');
      setName('');
      onCreated(site);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a new site"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button form="new-site-form" type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create site'}
          </button>
        </>
      }
    >
      <form id="new-site-form" onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My new product"
            required
            autoFocus
          />
          <p className="mt-1 text-xs text-ink-500">
            We'll generate a URL slug and a starter Home page for you.
          </p>
        </div>
      </form>
    </Modal>
  );
}
